import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Button,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";

import { colors } from "@/common/colors";
import { TIME_TYPE } from "@/components/alerts/constants";

const IncidentTimeSelector = ({
  incidentTimes,
  setIncidentTimes,
  errors,
}: {
  incidentTimes: {
    time_type: string;
    start_date: Date | string | null;
    end_date: Date | string | null;
  }[];
  setIncidentTimes: (
    incidentTimes: {
      time_type: string;
      start_date: Date | string | null;
      end_date: Date | string | null;
    }[],
  ) => void;
  errors: boolean;
}) => {
  const { t } = useTranslation([
    "bulletin/create/datePicker",
    "bulletin/create",
  ]);

  const helperTextStyling = {
    style: {
      color: colors.ERROR_TEXT_RED,
      marginLeft: "10px",
    },
  };

  const handleAddTime = () => {
    setIncidentTimes([
      ...incidentTimes,
      { time_type: "exact", start_date: null, end_date: null },
    ]);
  };

  const handleDeleteTime = (index: number) => {
    const updatedTimes = incidentTimes.filter((_, i) => i !== index);
    setIncidentTimes(updatedTimes);
  };

  const handleDateChange = (
    index: number,
    key: "start_date" | "end_date",
    value: Date | null,
  ) => {
    const updatedTimes = [...incidentTimes];
    updatedTimes[index][key] = value;
    setIncidentTimes(updatedTimes);
  };

  const handleTimeTypeChange = (
    index: number,
    value: string | null | undefined,
  ) => {
    if (value) {
      const updatedTimes = [...incidentTimes];
      updatedTimes[index].time_type = value;
      if (value === "unknown" || value === "not_applicable") {
        updatedTimes[index].start_date = null;
        updatedTimes[index].end_date = null;
      } else if (value === "time_unknown") {
        const existing = updatedTimes[index].start_date;
        if (existing) {
          const dateOnly = new Date(existing);
          dateOnly.setHours(0, 0, 0, 0);
          updatedTimes[index].start_date = dateOnly;
        }
        updatedTimes[index].end_date = null;
      }
      setIncidentTimes(updatedTimes);
    }
  };

  const dateTimePicker = (
    time: {
      time_type: string;
      start_date: Date | string | null;
      end_date: Date | string | null;
    },
    index: number,
  ) => {
    const startDate =
      typeof time.start_date === "string"
        ? new Date(time.start_date)
        : time.start_date;
    const endDate =
      typeof time.end_date === "string"
        ? new Date(time.end_date)
        : time.end_date;

    const getShowError = () => {
      if (!errors) return false;

      switch (time.time_type) {
        case "range":
          return !time.start_date || !time.end_date;
        case "time_unknown":
        case "exact":
          return !time.start_date;
        case "not_applicable":
        case "unknown":
          return false;
        default:
          return !time.start_date;
      }
    };

    const showError = getShowError();

    switch (time.time_type) {
      case "range":
        return (
          <div style={{ display: "flex", gap: "10px", flexGrow: 1 }}>
            {/* Start Time Picker */}
            <ReactDatePicker
              selected={startDate}
              className="start-date-picker"
              showTimeSelect
              timeIntervals={15}
              selectsStart
              timeCaption={t("start_date")}
              dateFormat="Pp"
              maxDate={new Date()}
              placeholderText={t("select_start_date")}
              onChange={(date) => handleDateChange(index, "start_date", date)}
              customInput={
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t("start_date")}
                  error={showError}
                  required
                  helperText={
                    showError
                      ? t("bulletin/create:error_message_invalid_incident_date")
                      : ""
                  }
                  FormHelperTextProps={helperTextStyling}
                />
              }
            />

            {/* End Time Picker */}
            <ReactDatePicker
              selected={endDate}
              showTimeSelect
              timeIntervals={15}
              timeCaption={t("end_date")}
              dateFormat="Pp"
              selectsEnd
              maxDate={new Date()}
              minDate={startDate ?? undefined}
              placeholderText={t("select_end_date")}
              onChange={(date) => handleDateChange(index, "end_date", date)}
              customInput={
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t("end_date")}
                  error={showError}
                  required
                  helperText={
                    showError
                      ? t("bulletin/create:error_message_invalid_incident_date")
                      : ""
                  }
                  FormHelperTextProps={helperTextStyling}
                />
              }
            />
          </div>
        );

      case "time_unknown":
        return (
          <ReactDatePicker
            selected={startDate}
            onChange={(date) => handleDateChange(index, "start_date", date)}
            showTimeSelect={false}
            dateFormat="yyyy/MM/dd"
            wrapperClassName="w-100"
            maxDate={new Date()}
            customInput={
              <TextField
                fullWidth
                variant="outlined"
                label={t("select_date")}
                value={time.start_date}
                error={showError}
                required
                helperText={
                  showError
                    ? t("bulletin/create:error_message_invalid_incident_date")
                    : ""
                }
                FormHelperTextProps={helperTextStyling}
              />
            }
          />
        );
      case "not_applicable":
      case "unknown":
        return (
          <TextField
            fullWidth
            disabled
            variant="outlined"
            value="No date/time required"
            label="Date/Time"
          />
        );
      default:
        return (
          <ReactDatePicker
            selected={startDate}
            onChange={(date) => handleDateChange(index, "start_date", date)}
            showTimeSelect
            dateFormat="Pp"
            maxDate={new Date()}
            wrapperClassName="w-100"
            customInput={
              <TextField
                fullWidth
                variant="outlined"
                label={t("start_date")}
                value={time.start_date}
                error={showError}
                required
                helperText={
                  showError
                    ? t("bulletin/create:error_message_invalid_incident_date")
                    : ""
                }
                FormHelperTextProps={helperTextStyling}
              />
            }
          />
        );
    }
  };

  return (
    <div className="w-100">
      {incidentTimes.map((time, index) => (
        <div
          key={index}
          className="w-100"
          style={{
            position: "relative",
          }}
        >
          <Typography variant="subtitle1">
            {t("incident_time", { Index: index + 1 })}
          </Typography>

          <div className="d-flex w-100" style={{ gap: "10px" }}>
            {/* Time Type Selector */}
            <Autocomplete
              value={time.time_type}
              className="w-30"
              options={Object.values(TIME_TYPE)}
              getOptionLabel={(option) => t(option)}
              openOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t("time_type")}
                  error={errors && !time.time_type}
                  required
                  helperText={
                    errors && !time.time_type
                      ? t("bulletin/create:error_message_invalid_incident_date")
                      : ""
                  }
                  FormHelperTextProps={helperTextStyling}
                />
              )}
              renderOption={({ key, ...props }, option) => (
                <li key={key} value={option} {...props}>
                  {t(option)}
                </li>
              )}
              onChange={(e, value) => handleTimeTypeChange(index, value)}
            />

            {/* React Datepicker for Start Time */}
            <div className="w-70">{dateTimePicker(time, index)}</div>
            {incidentTimes.length > 1 && (
              <IconButton
                color="secondary"
                title="delete"
                onClick={() => handleDeleteTime(index)}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            )}
          </div>
        </div>
      ))}

      {/* Add Another Time Button */}
      <Button onClick={handleAddTime} color="secondary">
        {t("add_another_date_time")}
      </Button>
    </div>
  );
};

export default IncidentTimeSelector;
